import {
  getRouteApi,
  HeadContent,
  Outlet,
  Scripts,
} from '@tanstack/react-router';
import {
  graphql,
  RelayEnvironmentProvider,
  usePreloadedQuery,
} from 'react-relay';
import { Toaster } from 'sonner';

import { AmpliProvider } from '@/ampli';
import ConsentBanner from '@/components/ConsentBanner';
import GoogleAdsTag from '@/components/GoogleAdsTag';
import LinkedInInsightTag from '@/components/LinkedInInsightTag';
import MetaPixel from '@/components/MetaPixel';
import SignupConversionTag from '@/components/SignupConversionTag';
import useCaptureWaitlistUtm from '@/hooks/use-capture-waitlist-utm';
import { useLoadedData } from '@/loader';
import StatsigProvider from '@/providers/StatsigProvider';

import type { RootPageQuery } from './__generated__/RootPageQuery.graphql';

const routeApi = getRouteApi('__root__');

const RootPageProviders = () => {
  const loaderData = routeApi.useLoaderData();
  useCaptureWaitlistUtm();

  const rootQueryRef = useLoadedData<RootPageQuery>(loaderData.root);
  const data = usePreloadedQuery<RootPageQuery>(
    graphql`
      query RootPageQuery {
        ...StatsigProvider_query
      }
    `,
    rootQueryRef,
  );

  return (
    <StatsigProvider query={data}>
      <AmpliProvider>
        <Outlet />
        <ConsentBanner />
        <GoogleAdsTag />
        <LinkedInInsightTag />
        <MetaPixel />
        <SignupConversionTag />
        <Toaster position="top-center" richColors />
      </AmpliProvider>
    </StatsigProvider>
  );
};

const RootPage = () => {
  const { relayEnvironment } = routeApi.useRouteContext();

  return (
    <html lang="en-GB">
      <head>
        <HeadContent />
      </head>
      <body>
        <RelayEnvironmentProvider environment={relayEnvironment}>
          <RootPageProviders />
        </RelayEnvironmentProvider>
        <Scripts />
      </body>
    </html>
  );
};

export default RootPage;
